import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api';
import { getTranslation } from '../i18n';
import LanguageTabs from '../components/LanguageTabs';
import TranslateFromEnButton from '../components/TranslateFromEnButton';
import { TEXT_LANGS, normalizeByLang } from '../utils/clientTexts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyWelcome = () => ({
  welcome_title: '',
  welcome_subtitle: '',
  welcome_description: '',
});

const BrandingSettings = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Branding';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Resolved branding (template + overrides)
  const [resolved, setResolved] = useState(null);
  const [canCustomize, setCanCustomize] = useState(false);
  // Pure template default texts (per-language) — used for "Default: …" hints
  // and Reset behavior.
  const [templateTexts, setTemplateTexts] = useState({});

  // Editable form state
  const [workspaceName, setWorkspaceName] = useState('');
  // Welcome texts stored per-language: { en:{...}, pl:{...}, de:{...} }
  const [welcomeByLang, setWelcomeByLang] = useState({
    en: emptyWelcome(), pl: emptyWelcome(), de: emptyWelcome(),
  });
  const [editLang, setEditLang] = useState('en');
  const [logoUrl, setLogoUrl] = useState('');
  const [allowLogoUpload, setAllowLogoUpload] = useState(false);
  const [hasOverrides, setHasOverrides] = useState(false);

  const [workspaceLang, setWorkspaceLang] = useState('en');
  const t = getTranslation(workspaceLang);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const [brandingRes, settingsRes] = await Promise.all([
        authAPI.getBranding(),
        authAPI.getSettings(),
      ]);
      const { resolved: r, overrides, can_customize_branding, template_texts } = brandingRes.data;
      setResolved(r);
      setCanCustomize(can_customize_branding);
      setAllowLogoUpload(r.allow_logo_upload || false);
      setWorkspaceLang(settingsRes.data.language || 'en');
      setEditLang(settingsRes.data.language || 'en');
      setTemplateTexts(normalizeByLang(template_texts || {}));
      // Helper: resolve the default text for a field+lang (falls back to EN)
      const tplNorm = normalizeByLang(template_texts || {});
      const defOf = (lang, key) => {
        const ld = tplNorm?.[lang] || {};
        if (ld[key]) return ld[key];
        const en = tplNorm?.en || {};
        return en[key] || '';
      };

      // Populate form with resolved values
      setWorkspaceName(r.workspace_name || '');
      setLogoUrl(r.workspace_logo_url || '');

      const normalizedOverrides = normalizeByLang(overrides?.texts);
      const seeded = {};
      for (const lang of TEXT_LANGS) {
        const langTexts = normalizedOverrides[lang] || {};
        // Pre-fill fields with the user's override OR the real template default
        // so the textarea always shows the actual editable content (no ghost
        // text). Distinguish "untouched" (undefined/null → fall back to
        // template default) from "intentionally cleared" ("" → keep ""), so
        // a saved empty override does NOT bounce back to the default on reload.
        const overrideOr = (key) =>
          langTexts[key] === undefined || langTexts[key] === null
            ? defOf(lang, key)
            : langTexts[key];
        seeded[lang] = {
          welcome_title:       overrideOr('welcome_title'),
          welcome_subtitle:    overrideOr('welcome_subtitle'),
          welcome_description: overrideOr('welcome_description'),
        };
      }
      setWelcomeByLang(seeded);
      setHasOverrides(Object.keys(overrides || {}).length > 0);
    } catch (err) {
      console.error('Error fetching branding:', err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const current = welcomeByLang[editLang] || emptyWelcome();
  const updateField = (key, value) => {
    setWelcomeByLang(prev => ({
      ...prev,
      [editLang]: { ...(prev[editLang] || {}), [key]: value },
    }));
  };

  // Look up the default value for a given welcome-text field in the current
  // edit language. Falls back to EN if the language doesn't have its own copy.
  const getDefault = (key) => {
    const langDefaults = templateTexts?.[editLang] || {};
    if (langDefaults[key]) return langDefaults[key];
    const enDefaults = templateTexts?.en || {};
    return enDefaults[key] || '';
  };

  // A field is "Custom" when the user value differs from the template default
  // for the current language. An intentional empty clear ("" when default is
  // non-empty) also counts as custom — the user has explicitly overridden it.
  const isCustom = (key) => {
    const v = current[key];
    if (v === undefined || v === null) return false;
    const def = getDefault(key) || '';
    return v !== def;
  };

  // Reset replaces the field with the actual default text (not empty),
  // so users always see real editable content.
  const resetFieldToDefault = (key) => {
    updateField(key, getDefault(key) || '');
  };

  const resetAllTextsToDefault = () => {
    const fresh = {};
    for (const lang of TEXT_LANGS) {
      const ld = templateTexts?.[lang] || {};
      const en = templateTexts?.en || {};
      fresh[lang] = {
        welcome_title:       ld.welcome_title       || en.welcome_title       || '',
        welcome_subtitle:    ld.welcome_subtitle    || en.welcome_subtitle    || '',
        welcome_description: ld.welcome_description || en.welcome_description || '',
      };
    }
    setWelcomeByLang(fresh);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Brand name is always sent (universal right). Advanced texts only when
      // the plan allows — server also enforces this as a hard gate.
      const data = { workspace_name: workspaceName };

      if (canCustomize) {
        const textsByLang = {};
        for (const lang of TEXT_LANGS) {
          const langData = welcomeByLang[lang] || {};
          const ld = templateTexts?.[lang] || {};
          const en = templateTexts?.en || {};
          const clean = {};
          for (const [k, vRaw] of Object.entries(langData)) {
            const v = vRaw == null ? '' : String(vRaw);
            const def = (ld[k] || en[k] || '');
            // Field matches the template default → no override needed.
            // Field differs from default (custom string OR intentional "")
            // → persist as override so the user's intent survives reload.
            if (v === def) continue;
            clean[k] = v;
          }
          textsByLang[lang] = clean;
        }
        data.texts = textsByLang;
      }

      const { data: result } = await authAPI.updateBranding(data);
      setResolved(result.resolved);
      setHasOverrides(Object.keys(result.overrides || {}).length > 0);
      showToast('Branding saved');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const { data: result } = await authAPI.resetBranding();
      setResolved(result.resolved);
      setWorkspaceName(result.resolved.workspace_name || '');
      setLogoUrl(result.resolved.workspace_logo_url || '');
      // Re-seed editable fields with the freshly-resolved template defaults so
      // the user keeps seeing real text after reset, not blank inputs.
      const tplNorm = normalizeByLang(result.template_texts || {});
      const fresh = {};
      for (const lang of TEXT_LANGS) {
        const ld = tplNorm?.[lang] || {};
        const en = tplNorm?.en || {};
        fresh[lang] = {
          welcome_title:       ld.welcome_title       || en.welcome_title       || '',
          welcome_subtitle:    ld.welcome_subtitle    || en.welcome_subtitle    || '',
          welcome_description: ld.welcome_description || en.welcome_description || '',
        };
      }
      setTemplateTexts(tplNorm);
      setWelcomeByLang(fresh);
      setHasOverrides(false);
      showToast('Branding reset to template defaults');
    } catch (err) {
      showToast('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await authAPI.uploadBrandingLogo(file);
      setLogoUrl(data.logo_url);
      showToast('Logo uploaded');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleLogoDelete = async () => {
    try {
      await authAPI.deleteBrandingLogo();
      setLogoUrl('');
      showToast('Logo removed');
    } catch (err) {
      showToast('Failed to remove logo');
    }
  };

  if (loading) {
    return (
      <div className="dashboard fade-in" style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  const isLocked = !canCustomize;

  return (
    <div className="dashboard fade-in" style={{ maxWidth: '720px', margin: '0 auto' }}>
      <header className="dashboard-header">
        <div>
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '12px',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '8px'
          }}>{resolved?.workspace_name || t.studio_name}</p>
          <h1 className="dashboard-title">Branding Settings</h1>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button className="btn-secondary" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            Back to Dashboard
          </button>
        </div>
      </header>

      {isLocked && (
        <div data-testid="branding-locked" style={{
          background: '#f8f6f3',
          border: '1px solid var(--kahla-border)',
          padding: '24px',
          textAlign: 'center',
          marginBottom: '32px',
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.5 }}>
            <i className="fa-solid fa-lock" />
          </div>
          <p style={{ fontSize: '14px', color: 'var(--kahla-text)', marginBottom: '4px', fontWeight: '500' }}>
            Advanced branding is not available on your current plan
          </p>
          <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>
            You can still edit your brand name below. Logo, colors, and custom texts unlock with the Branding plan.
          </p>
        </div>
      )}

      {/* Brand Identity — ALWAYS editable at every plan level */}
      <Section title="Brand Identity">
        <Field label="Brand name" hint="Shown in client view, branding header, and exports.">
          <input
            data-testid="branding-workspace-name"
            type="text"
            value={workspaceName}
            onChange={e => setWorkspaceName(e.target.value)}
            placeholder="e.g. My Photo Studio"
            style={inputStyle}
          />
        </Field>
      </Section>

      <div style={{ opacity: isLocked ? 0.5 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
        {/* Logo */}
        {allowLogoUpload && (
          <Section title="Workspace Logo">
            <Field label="Logo" hint="Displayed in client views and PDF exports. Max 2MB.">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {logoUrl && (
                  <div style={{
                    width: '80px', height: '48px',
                    border: '1px solid var(--kahla-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', background: '#fff',
                  }}>
                    <img
                      src={logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}/api/images/${logoUrl}`}
                      alt="Logo"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </div>
                )}
                <label data-testid="branding-logo-upload" style={{
                  ...actionBtnStyle,
                  cursor: 'pointer',
                  display: 'inline-block',
                }}>
                  {logoUrl ? 'Change' : 'Upload'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoUpload} style={{ display: 'none' }} />
                </label>
                {logoUrl && (
                  <button data-testid="branding-logo-delete" onClick={handleLogoDelete} style={{ ...actionBtnStyle, color: '#c62828', borderColor: '#c6282820' }}>
                    Remove
                  </button>
                )}
              </div>
            </Field>
          </Section>
        )}

        {/* Welcome Texts */}
        <Section title="Welcome Texts" subtitle="Override default template texts for your client boards. Edit each language separately.">
          <LanguageTabs value={editLang} onChange={setEditLang} testId="branding-lang-tabs" />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '4px',
          }}>
            <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic', margin: 0 }}>
              Editing overrides for <strong style={{ color: 'var(--kahla-charcoal)' }}>{editLang.toUpperCase()}</strong>. Empty fields fall back to the template default.
            </p>
            <button
              type="button"
              data-testid="reset-all-texts-btn"
              onClick={resetAllTextsToDefault}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: '11px',
                color: 'var(--kahla-taupe)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(166,152,136,0.4)',
                textUnderlineOffset: '3px',
                whiteSpace: 'nowrap',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
              title="Reset all welcome texts back to the template default for every language"
            >
              Reset all to default
            </button>
          </div>
          <TranslateFromEnButton
            targetLang={editLang}
            testId="branding-translate-btn"
            t={t}
            getSourceTexts={() => {
              // Use effective EN texts (override OR template default) so
              // translation works even after "Reset to default".
              const enOverride = welcomeByLang.en || {};
              const enTemplate = templateTexts?.en || {};
              const merged = {};
              for (const k of Object.keys(enOverride)) {
                const v = (enOverride[k] || '').trim();
                merged[k] = v || (enTemplate[k] || '');
              }
              return merged;
            }}
            getTargetTexts={() => welcomeByLang[editLang] || {}}
            onTranslated={(translations) => {
              setWelcomeByLang(prev => ({
                ...prev,
                [editLang]: { ...(prev[editLang] || {}), ...translations },
              }));
            }}
          />
          <WelcomeField
            label="Welcome Title"
            testId="branding-welcome-title"
            value={current.welcome_title}
            onChange={(v) => updateField('welcome_title', v)}
            defaultValue={getDefault('welcome_title')}
            isCustom={isCustom('welcome_title')}
            onUseDefault={() => resetFieldToDefault('welcome_title')}
          />
          <WelcomeField
            label="Welcome Subtitle"
            testId="branding-welcome-subtitle"
            value={current.welcome_subtitle}
            onChange={(v) => updateField('welcome_subtitle', v)}
            defaultValue={getDefault('welcome_subtitle')}
            isCustom={isCustom('welcome_subtitle')}
            onUseDefault={() => resetFieldToDefault('welcome_subtitle')}
            multiline
          />
          <WelcomeField
            label="Welcome Description"
            testId="branding-welcome-description"
            value={current.welcome_description}
            onChange={(v) => updateField('welcome_description', v)}
            defaultValue={getDefault('welcome_description')}
            isCustom={isCustom('welcome_description')}
            onUseDefault={() => resetFieldToDefault('welcome_description')}
          />
        </Section>
      </div>

      {/* Actions — always enabled so brand name can be saved at any plan */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px', marginBottom: '40px' }}>
        {hasOverrides && (
          <button
            data-testid="branding-reset-btn"
            className="btn-secondary"
            onClick={handleReset}
            disabled={saving}
          >
            Reset to Template Defaults
          </button>
        )}
        <button
          data-testid="branding-save-btn"
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

// ==================== UI Helpers ====================

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '13px',
  border: '1px solid var(--kahla-border)',
  background: 'var(--kahla-white)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const actionBtnStyle = {
  padding: '6px 14px',
  fontSize: '12px',
  border: '1px solid var(--kahla-border)',
  background: 'none',
  color: 'var(--kahla-text)',
  fontFamily: 'inherit',
};

const Section = ({ title, subtitle, children }) => (
  <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--kahla-border)' }}>
    <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '600', color: 'var(--kahla-text)', marginBottom: subtitle ? '4px' : '16px' }}>{title}</h2>
    {subtitle && <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>{subtitle}</p>}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: 'var(--kahla-text)', marginBottom: '6px' }}>
      {label}
    </label>
    {hint && <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '6px' }}>{hint}</p>}
    {children}
  </div>
);

/**
 * Editorial welcome-text field with:
 *   • "Custom" badge when the value differs from the template default
 *   • inline "Use default" reset link (only when the field is Custom)
 *   • slightly stronger border when the field is Custom for at-a-glance state
 *
 * Fields are pre-filled with the actual template default text on load, so
 * users edit real content directly. No fake ghost text below.
 */
const WelcomeField = ({
  label,
  testId,
  value,
  onChange,
  defaultValue,
  isCustom,
  onUseDefault,
  multiline = false,
}) => {
  const Tag = multiline ? 'textarea' : 'input';
  const fieldInputStyle = {
    ...inputStyle,
    borderColor: isCustom ? 'var(--kahla-charcoal)' : 'var(--kahla-border)',
    ...(multiline ? { minHeight: '60px', resize: 'vertical' } : {}),
  };
  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        marginBottom: '6px',
      }}>
        <label style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          fontWeight: 500,
          color: 'var(--kahla-text)',
        }}>
          {label}
          {isCustom && (
            <span
              data-testid={`${testId}-custom-badge`}
              style={{
                fontSize: '9px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--kahla-charcoal)',
                border: '1px solid var(--kahla-charcoal)',
                padding: '2px 6px',
                lineHeight: 1,
              }}
            >
              Custom
            </span>
          )}
        </label>
        {isCustom && (
          <button
            type="button"
            data-testid={`${testId}-use-default-btn`}
            onClick={onUseDefault}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: '11px',
              color: 'var(--kahla-taupe)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(166,152,136,0.4)',
              textUnderlineOffset: '3px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
          >
            Use default
          </button>
        )}
      </div>
      <Tag
        data-testid={testId}
        type={multiline ? undefined : 'text'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder=""
        style={fieldInputStyle}
      />
    </div>
  );
};

export default BrandingSettings;
